import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import { LogOut, LogIn, Menu, X, Facebook } from "lucide-react";
import { useState } from "react";

const NAV_ITEMS = [
  { href: "#home", label: "Нүүр" },
  { href: "#search", label: "Хайлт" },
  { href: "#listings", label: "Жагсаалт" },
  { href: "#for-sale", label: "Зар" },
  { href: "#about", label: "Бидний тухай" },
  { href: "#submit", label: "Мэдээлэл илгээх" },
  { href: "#contact", label: "Холбоо барих" },
];

const LandingLayout = ({ children }) => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const onNavClick = (e, href) => {
    setMobileOpen(false);
    if (!isHome) {
      // Let normal link navigation happen if not on home
      return;
    }
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBF9F6]">
      <header className="backdrop-blur-xl bg-[#FBF9F6]/90 border-b border-[#B8860B]/25 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
            <img src="/logo.png" alt="Дарьганга зээрднүүд" className="h-14 w-auto object-contain" />
            <div className="leading-tight hidden sm:block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#B8860B] font-bold">Адууны угийн бичиг</div>
              <div className="text-base font-bold text-[#2C241B]">Дарьганга зээрднүүд</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((n) => (
              <a
                key={n.href}
                href={isHome ? n.href : `/${n.href}`}
                onClick={(e) => onNavClick(e, n.href)}
                className="text-sm text-[#2C241B] hover:text-[#B8860B] px-3 py-2 transition-colors"
                data-testid={`nav-${n.href.slice(1)}`}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {admin ? (
              <>
                <Link
                  to="/admin"
                  className="hidden sm:inline-block text-sm text-[#1B3B5A] hover:text-[#B8860B] px-3 py-2 transition-colors font-semibold"
                  data-testid="nav-admin"
                >
                  Удирдлага
                </Link>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center gap-1 text-sm text-[#5C4F42] hover:text-[#8B2500] transition-colors"
                  data-testid="nav-logout"
                >
                  <LogOut size={16} strokeWidth={1.5} /> <span className="hidden sm:inline">Гарах</span>
                </button>
              </>
            ) : (
              <Link
                to="/admin/login"
                className="inline-flex items-center gap-1 text-sm text-[#1B3B5A] hover:text-[#B8860B] transition-colors"
                data-testid="nav-login"
              >
                <LogIn size={16} strokeWidth={1.5} /> Админ
              </Link>
            )}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden p-2 text-[#2C241B]"
              aria-label="menu"
              data-testid="nav-mobile-toggle"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="lg:hidden border-t border-[#B8860B]/20 bg-[#FBF9F6]">
            <div className="px-4 py-3 flex flex-col">
              {NAV_ITEMS.map((n) => (
                <a
                  key={n.href}
                  href={isHome ? n.href : `/${n.href}`}
                  onClick={(e) => onNavClick(e, n.href)}
                  className="py-2 text-sm text-[#2C241B] hover:text-[#B8860B] border-b border-[#D6CFC4] last:border-0"
                >
                  {n.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer id="contact" className="border-t border-[#B8860B]/20 bg-[#F0EBE1]/40 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="flex items-start gap-3">
            <img src="/logo.png" alt="logo" className="h-20 w-auto object-contain" />
            <div>
              <div className="font-bold text-[#2C241B]">Дарьганга Зээрднүүд хурдан адуу, үржлийн аж ахуй</div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#B8860B] mb-3 font-bold">Холбоо барих</div>
            <div className="text-sm text-[#2C241B] break-all" data-testid="footer-email">
              Имэйл:{" "}
              <a href="mailto:Dariganga.zeerdnuud@gmail.com" className="text-[#1B3B5A] hover:text-[#B8860B] underline">
                Dariganga.zeerdnuud@gmail.com
              </a>
            </div>
            <div className="text-sm text-[#2C241B] mt-1" data-testid="footer-phone">
              Утас:{" "}
              <a href="tel:+97689904013" className="text-[#1B3B5A] hover:text-[#B8860B]">89904013</a>
              {", "}
              <a href="tel:+97690434013" className="text-[#1B3B5A] hover:text-[#B8860B]">90434013</a>
            </div>
            <div className="text-sm text-[#2C241B] mt-2">
              <a
                href="https://www.facebook.com/share/1DgzT3WwE1/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[#1B3B5A] hover:text-[#B8860B] font-bold"
                data-testid="footer-facebook"
              >
                <Facebook size={16} strokeWidth={1.5} /> Facebook
              </a>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#B8860B] mb-3 font-bold">Холбоос</div>
            <div className="flex flex-col gap-1 text-sm">
              <a href={isHome ? "#about" : "/#about"} className="text-[#2C241B] hover:text-[#B8860B]">Бидний тухай</a>
              <a href={isHome ? "#submit" : "/#submit"} className="text-[#2C241B] hover:text-[#B8860B]">Мэдээлэл илгээх</a>
              <Link to="/admin/login" className="text-[#2C241B] hover:text-[#B8860B]">Админ нэвтрэх</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-[#D6CFC4] py-4 text-center text-xs text-[#5C4F42]">
          © {new Date().getFullYear()} Дарьганга Зээрднүүд хурдан адуу, үржлийн аж ахуй
        </div>
      </footer>
    </div>
  );
};

export default LandingLayout;
